/**
 * Mark a horizontally-scrollable element with a `data-edge` attribute so CSS
 * can fade whichever side still has content scrolled out of view. This is the
 * affordance that tells a phone user the row keeps going.
 *
 *   data-edge="none"  — nothing to scroll (fits, or non-mobile)
 *   data-edge="start" — at the left, more to the right
 *   data-edge="mid"   — more in both directions
 *   data-edge="end"   — at the right, more to the left
 *
 * Returns a disposer that detaches the listeners.
 */
export function attachEdgeFade(el: HTMLElement): () => void {
  const update = () => {
    const max = el.scrollWidth - el.clientWidth;
    if (max <= 1) {
      el.dataset.edge = 'none';
      return;
    }
    const x = el.scrollLeft;
    el.dataset.edge = x <= 1 ? 'start' : x >= max - 1 ? 'end' : 'mid';
  };

  el.addEventListener('scroll', update, { passive: true });
  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
  ro?.observe(el);
  update();

  return () => {
    el.removeEventListener('scroll', update);
    ro?.disconnect();
  };
}
